// The paket proposal card (Penyedia paket project): what Pluto read from an
// LPSE announcement, a dokumen pemilihan or the user's description, as
// editable fields (paket name, instansi, HPS in Rupiah, the deadline, the
// LPSE link), each with the text it came from; fields Pluto could not find
// are empty and highlighted. The announcement details it found (kode paket,
// kode RUP, LPSE, lokasi, jenis, metode, kualifikasi, pagu, the hour of the
// deadline, durasi, status, KAK summary) are listed and saved with them;
// the ones not found are left as they are. The requirements it found are
// checkboxes for "Tambahkan ke checklist Pelaksanaan Pemilihan". Simpan ke project saves through the paket
// context (PATCH /projects/:id/paket); when the project already holds other
// values, a confirmation lists what changes first. Saved, the card says
// Tersimpan and the page's paket header follows.
import { useId, useMemo, useState, type ReactNode } from "react";
import * as API from "../api";
import type { PaketProposal, PaketSaveInput, Project } from "../api";
import { formatCount, formatRupiah } from "../lib/format";
import { Badge, Dialog, Field, Notice } from "../ui/components";
import { Glyph } from "../ui/icons";
import { usePaket } from "./PaketContext";
import { hpsDigits, isLink } from "./PaketEditDialog";
import { JadwalTable, StageSuggestion } from "./PaketJadwal";
import { JENIS_LABEL, KUALIFIKASI_LABEL, METODE_LABEL, formatDeadline } from "./stages";

type Key = "paketName" | "instansi" | "nilaiHps" | "deadline" | "sumberUrl";

interface Form {
  paketName: string;
  instansi: string;
  /** Digits only. */
  nilaiHps: string;
  /** YYYY-MM-DD. */
  deadline: string;
  sumberUrl: string;
}

const LABEL: Record<Key, string> = {
  paketName: "Nama paket",
  instansi: "Instansi",
  nilaiHps: "Nilai HPS (Rp)",
  deadline: "Batas penawaran",
  sumberUrl: "Tautan LPSE",
};

const KIND_LABEL: Record<string, string> = {
  kbli: "KBLI",
  sbu: "SBU",
  pengalaman: "Pengalaman",
  personel: "Personel",
  peralatan: "Peralatan",
  jaminan: "Jaminan",
  lainnya: "Lainnya",
};

function formOf(p: PaketProposal): Form {
  return {
    paketName: p.paketName.value ?? "",
    instansi: p.instansi.value ?? "",
    nilaiHps: p.nilaiHps.value == null ? "" : String(Math.round(p.nilaiHps.value)),
    deadline: p.deadline.value ?? "",
    sumberUrl: p.sumberUrl.value ?? "",
  };
}

function inputOf(f: Form): Required<Pick<PaketSaveInput, Key>> {
  return {
    paketName: f.paketName.trim() || null,
    instansi: f.instansi.trim() || null,
    nilaiHps: f.nilaiHps ? Number(f.nilaiHps) : null,
    deadline: f.deadline || null,
    sumberUrl: f.sumberUrl.trim() || null,
  };
}

type ExtraKey = "kodePaket" | "kodeRup" | "lpse" | "lokasi" | "jenisPengadaan" | "metode" | "kualifikasi" | "pagu" | "deadlineTime" | "durasiHari" | "statusPaket" | "ringkasanKak";

const EXTRA_LABEL: Record<ExtraKey, string> = {
  kodePaket: "Kode paket",
  kodeRup: "Kode RUP",
  lpse: "LPSE",
  lokasi: "Lokasi",
  jenisPengadaan: "Jenis pengadaan",
  metode: "Metode",
  kualifikasi: "Kualifikasi",
  pagu: "Pagu",
  deadlineTime: "Jam batas penawaran",
  durasiHari: "Durasi",
  statusPaket: "Status paket",
  ringkasanKak: "Ringkasan KAK",
};

type Extras = Partial<Pick<PaketSaveInput, ExtraKey>>;

/** The announcement details Pluto found (older proposals have none). */
function extrasOf(p: PaketProposal): Extras {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(EXTRA_LABEL) as ExtraKey[]) {
    const value = p[key]?.value;
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out as Extras;
}

function shownExtra(key: ExtraKey, value: unknown): string {
  if (key === "jenisPengadaan") return JENIS_LABEL[value as keyof typeof JENIS_LABEL] ?? String(value);
  if (key === "metode") return METODE_LABEL[value as keyof typeof METODE_LABEL] ?? String(value);
  if (key === "kualifikasi") return KUALIFIKASI_LABEL[value as keyof typeof KUALIFIKASI_LABEL] ?? String(value);
  if (key === "pagu") return formatRupiah(Number(value));
  if (key === "durasiHari") return `${value} hari`;
  return String(value);
}

function shown(key: Key, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "kosong";
  if (key === "nilaiHps") return formatRupiah(Number(value));
  if (key === "deadline") return formatDeadline(String(value));
  return String(value);
}

/** Fields the save would change from a value the project already holds. */
function overwrites(project: Project, input: ReturnType<typeof inputOf>, extras: Extras): { label: string; from: string; to: string }[] {
  const out: { label: string; from: string; to: string }[] = [];
  for (const key of Object.keys(input) as Key[]) {
    const before = project[key] ?? null;
    const after = input[key];
    if (before !== null && before !== after) out.push({ label: LABEL[key], from: shown(key, before), to: shown(key, after) });
  }
  for (const key of Object.keys(extras) as ExtraKey[]) {
    const before = project[key] ?? null;
    const after = extras[key];
    if (before !== null && before !== after) out.push({ label: EXTRA_LABEL[key], from: shownExtra(key, before), to: shownExtra(key, after) });
  }
  return out;
}

function sameAsProject(project: Project, input: ReturnType<typeof inputOf>): boolean {
  return (Object.keys(input) as Key[]).every((key) => (project[key] ?? null) === input[key]);
}

export function PaketProposalCard({ proposal, messageId }: { proposal: PaketProposal; messageId: string }) {
  const paket = usePaket();
  const id = useId();
  const [form, setForm] = useState<Form>(() => formOf(proposal));
  const [picked, setPicked] = useState<boolean[]>(() => proposal.requirements.map(() => true));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ label: string; from: string; to: string }[] | null>(null);
  const extras = useMemo(() => extrasOf(proposal), [proposal]);
  const [savedNow, setSavedNow] = useState<{ added: number } | null>(null);
  const jadwal = proposal.jadwal ?? [];
  // The schedule's forward move, offered ticked; the API ignores it for a paket already there.
  const [move, setMove] = useState(proposal.stageSuggestion?.value === "pelaksanaan_pemilihan");
  const input = inputOf(form);
  const project = paket?.project ?? null;
  // A card whose values the project already holds reads as saved (after a reload too).
  const saved = savedNow !== null || (project !== null && sameAsProject(project, input) && Object.values(input).some((v) => v !== null));
  const missing = useMemo(() => new Set(proposal.missing as Key[]), [proposal.missing]);
  const linkBad = form.sumberUrl.trim() !== "" && !isLink(form.sumberUrl.trim());

  const set = (key: keyof Form, value: string) => {
    setSavedNow(null);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const doSave = async () => {
    if (!paket) return;
    setBusy(true);
    setError(null);
    try {
      const requirements = proposal.requirements.filter((_, i) => picked[i]).map((r) => r.text);
      const result = await paket.save({
        ...input,
        ...extras,
        ...(jadwal.length > 0 ? { jadwal } : {}),
        ...(move && proposal.stageSuggestion?.value === "pelaksanaan_pemilihan" ? { moveTo: "pelaksanaan_pemilihan" as const } : {}),
        ...(requirements.length > 0 ? { requirements } : {}),
      });
      setSavedNow({ added: result.added });
      setConfirm(null);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onSave = () => {
    if (!project || linkBad) return;
    const changes = overwrites(project, input, extras);
    if (changes.length > 0) setConfirm(changes);
    else void doSave();
  };

  const field = (key: Key, control: ReactNode) => {
    const raw = proposal[key].raw;
    const empty = key === "nilaiHps" ? !form.nilaiHps : !form[key];
    const hint = missing.has(key) && empty ? "Belum ditemukan. Isi bila sudah tahu." : raw ? `Dari teks: "${raw}"` : undefined;
    return (
      <div className="ppc-field" data-missing={missing.has(key) && empty ? "" : undefined}>
        <Field id={`${id}-${key}`} label={LABEL[key]} hint={hint} error={key === "sumberUrl" && linkBad ? "Tautan harus diawali http:// atau https://" : null}>
          {control}
        </Field>
      </div>
    );
  };

  return (
    <section className="ppc" data-saved={saved ? "" : undefined} aria-labelledby={`${id}-title`} data-message-id={messageId}>
      <div className="ppc-head">
        <h3 id={`${id}-title`} className="ppc-title">
          <Glyph name="box" size={18} />
          <span>Info paket dari Pluto</span>
        </h3>
        {saved ? <Badge tone="success">Tersimpan</Badge> : missing.size > 0 ? <Badge tone="warning">{missing.size} belum ditemukan</Badge> : null}
      </div>
      <div className="ppc-fields">
        {field(
          "paketName",
          <input id={`${id}-paketName`} value={form.paketName} maxLength={300} readOnly={busy} aria-describedby={`${id}-paketName-hint`} onChange={(e) => set("paketName", e.currentTarget.value)} />,
        )}
        {field(
          "instansi",
          <input id={`${id}-instansi`} value={form.instansi} maxLength={200} readOnly={busy} aria-describedby={`${id}-instansi-hint`} onChange={(e) => set("instansi", e.currentTarget.value)} />,
        )}
        <div className="pk-form-row">
          {field(
            "nilaiHps",
            <input
              id={`${id}-nilaiHps`}
              inputMode="numeric"
              placeholder="mis. 1.250.000.000"
              value={form.nilaiHps ? formatCount(Number(form.nilaiHps)) : ""}
              readOnly={busy}
              aria-describedby={`${id}-nilaiHps-hint`}
              onChange={(e) => set("nilaiHps", hpsDigits(e.currentTarget.value))}
            />,
          )}
          {field(
            "deadline",
            <input id={`${id}-deadline`} type="date" value={form.deadline} readOnly={busy} aria-describedby={`${id}-deadline-hint`} onChange={(e) => set("deadline", e.currentTarget.value)} />,
          )}
        </div>
        {field(
          "sumberUrl",
          <input id={`${id}-sumberUrl`} type="url" placeholder="https://lpse..." value={form.sumberUrl} maxLength={500} readOnly={busy} aria-describedby={`${id}-sumberUrl-hint`} onChange={(e) => set("sumberUrl", e.currentTarget.value)} />,
        )}
      </div>
      {Object.keys(extras).length > 0 ? (
        <div className="ppc-extras">
          <h4 className="ppc-extras-title">Rincian pengumuman</h4>
          <dl className="ppc-extra-list">
            {(Object.keys(extras) as ExtraKey[]).map((key) => (
              <div key={key} className="ppc-extra">
                <dt>{EXTRA_LABEL[key]}</dt>
                <dd>{shownExtra(key, extras[key])}</dd>
              </div>
            ))}
          </dl>
          <p className="ppc-extra-hint">Ikut tersimpan. Ubah lewat Ubah info paket bila perlu.</p>
        </div>
      ) : null}
      {jadwal.length > 0 ? (
        <div className="ppc-extras">
          <JadwalTable jadwal={jadwal} />
        </div>
      ) : null}
      <StageSuggestion
        suggestion={proposal.stageSuggestion}
        checked={move}
        disabled={busy || savedNow !== null}
        onChange={(on) => {
          setSavedNow(null);
          setMove(on);
        }}
      />
      {proposal.requirements.length > 0 ? (
        <fieldset className="ppc-reqs">
          <legend className="ppc-reqs-title">Tambahkan ke checklist Pelaksanaan Pemilihan</legend>
          <ul className="ppc-req-list">
            {proposal.requirements.map((r, i) => (
              <li key={`${r.kind}-${i}`}>
                <label className="ppc-req">
                  <input
                    type="checkbox"
                    checked={picked[i] ?? false}
                    disabled={busy}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setSavedNow(null);
                      setPicked((p) => p.map((v, n) => (n === i ? on : v)));
                    }}
                  />
                  <span className="ppc-req-text">{r.text}</span>
                  <span className="ppc-req-kind">{KIND_LABEL[r.kind] ?? r.kind}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}
      {error ? (
        <Notice tone="danger" title="Info paket belum tersimpan">
          <p>{error}</p>
        </Notice>
      ) : null}
      {savedNow ? (
        <p className="ppc-done" role="status">
          <Glyph name="tickCircle" size={16} />
          <span>
            Tersimpan ke project.
            {savedNow.added > 0 ? ` ${savedNow.added} persyaratan ditambahkan ke checklist Pelaksanaan Pemilihan.` : ""}
          </span>
        </p>
      ) : null}
      <div className="ppc-actions">
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={busy || !paket || linkBad || savedNow !== null} aria-busy={busy || undefined}>
          {savedNow ? "Tersimpan" : "Simpan ke project"}
        </button>
      </div>
      <Dialog
        open={confirm !== null}
        title="Ganti info paket?"
        size="sm"
        busy={busy}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={busy}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void doSave()} disabled={busy} aria-busy={busy || undefined}>
              Ganti
            </button>
          </>
        }
      >
        <p className="dialog-text">Project ini sudah berisi info paket. Nilai berikut akan diganti:</p>
        <ul className="ppc-changes">
          {(confirm ?? []).map((c) => (
            <li key={c.label}>
              <strong>{c.label}</strong>: {c.from} menjadi {c.to}
            </li>
          ))}
        </ul>
      </Dialog>
    </section>
  );
}
